// src/pages/FinanceDashboardPage.js - J49 Finance Manager Dashboard
import React, { useState, useEffect } from 'react';
import { eventService, authService, financeService } from '../services/api';
import { useRoleAccess } from '../hooks/useRoleAccess';
import '../styles/finance-dashboard.css';
import toast from 'react-hot-toast';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';

const FinanceDashboardPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [customLineItems, setCustomLineItems] = useState([]);
  const [overrideNotes, setOverrideNotes] = useState('');
  const [addingLineItem, setAddingLineItem] = useState(false);
  const [newLineItem, setNewLineItem] = useState({ description: '', amount: '' });
  const [saving, setSaving] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(false);
  useRoleAccess(); // Enforce role-based access control

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await eventService.getAllEvents();
      // Show all J49 events (BQM-created) AND API-created events
      const j49Events = (response.events || []).filter(event =>
        event.metadata?.isJ49Event || event.apiClientId
      );
      setEvents(j49Events);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalRevenue = () => {
    return events.reduce((sum, event) => {
      return sum + (event.metadata?.totalCost || 0);
    }, 0);
  };

  const getUpcomingEvents = () => {
    return events.filter(event => {
      const eventDate = new Date(event.startDate || event.date);
      return eventDate >= new Date();
    });
  };

  const getPastEvents = () => {
    return events.filter(event => {
      const eventDate = new Date(event.startDate || event.date);
      return eventDate < new Date();
    });
  };

  const filteredEvents = events.filter(event => {
    const searchLower = searchTerm.toLowerCase();
    return (
      event.name?.toLowerCase().includes(searchLower) ||
      event.venue?.toLowerCase().includes(searchLower)
    );
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const openOverrideModal = async (event) => {
    setSelectedEvent(event);

    // Load existing override data if available
    if (event.metadata?.financeOverride) {
      setCustomLineItems(event.metadata.financeOverride.customLineItems || []);
      setOverrideNotes(event.metadata.financeOverride.notes || '');
    } else {
      setCustomLineItems([]);
      setOverrideNotes('');
    }

    setShowOverrideModal(true);
  };

  const closeOverrideModal = () => {
    setShowOverrideModal(false);
    setSelectedEvent(null);
    setCustomLineItems([]);
    setOverrideNotes('');
    setAddingLineItem(false);
    setNewLineItem({ description: '', amount: '' });
  };

  const handleAddLineItem = () => {
    if (!newLineItem.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!newLineItem.amount || isNaN(newLineItem.amount)) {
      toast.error('Valid amount is required');
      return;
    }
    const amount = parseFloat(newLineItem.amount);
    if (amount < -100000 || amount > 100000) {
      toast.error('Amount must be between -$100,000 and $100,000');
      return;
    }

    setCustomLineItems([...customLineItems, {
      description: newLineItem.description.trim(),
      amount
    }]);
    setNewLineItem({ description: '', amount: '' });
    setAddingLineItem(false);
  };

  const handleRemoveLineItem = (index) => {
    setCustomLineItems(customLineItems.filter((_, i) => i !== index));
  };

  const calculateFinalTotal = () => {
    const baseTotal = selectedEvent?.metadata?.totalCost || 0;
    const adjustments = customLineItems.reduce((sum, item) => sum + item.amount, 0);
    return baseTotal + adjustments;
  };

  const handleSaveOverride = async () => {
    if (!selectedEvent) return;

    setSaving(true);
    try {
      await financeService.applyFinanceOverride(selectedEvent._id, {
        customLineItems,
        notes: overrideNotes.trim()
      });

      toast.success('Finance override saved successfully');
      await fetchEvents(); // Refresh event data
      closeOverrideModal();
    } catch (error) {
      toast.error(error.message || 'Failed to save finance override');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveOverride = async () => {
    if (!selectedEvent) return;
    setSaving(true);
    try {
      await financeService.removeFinanceOverride(selectedEvent._id);
      toast.success('Finance override removed successfully');
      setRemoveConfirm(false);
      await fetchEvents();
      closeOverrideModal();
    } catch (error) {
      toast.error(error.message || 'Failed to remove finance override');
    } finally {
      setSaving(false);
    }
  };

  const getPackageSummary = (event) => {
    const packages = event.metadata?.packages;
    if (!packages) return 'No packages selected';

    const selected = [];
    if (packages.food) selected.push('Food');
    if (packages.alcohol) selected.push('Bar');
    if (packages.music) selected.push('Music');
    if (packages.decor) selected.push('Decor');

    return selected.length > 0 ? selected.join(', ') : 'No packages';
  };

  if (loading) {
    return (
      <div className="finance-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading revenue data...</p>
      </div>
    );
  }

  const totalRevenue = calculateTotalRevenue();
  const upcomingEvents = getUpcomingEvents();
  const pastEvents = getPastEvents();

  return (
    <div className="finance-dashboard-page">
      {/* Header */}
      <div className="finance-header">
        <div className="finance-branding">
          <h1 className="finance-title">J49 Finance Dashboard</h1>
          <p className="finance-subtitle">Revenue & Booking Analytics</p>
        </div>
        <button className="logout-btn" onClick={() => {
          authService.logout();
          window.location.href = '/login';
        }}>
          Logout
        </button>
      </div>

      {/* Summary Cards */}
      <div className="finance-summary-cards">
        <div className="summary-card revenue-card">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <div className="card-value">{formatCurrency(totalRevenue)}</div>
            <div className="card-label">Total Revenue</div>
          </div>
        </div>

        <div className="summary-card bookings-card">
          <div className="card-icon">📅</div>
          <div className="card-content">
            <div className="card-value">{events.length}</div>
            <div className="card-label">Total Bookings</div>
          </div>
        </div>

        <div className="summary-card upcoming-card">
          <div className="card-icon">⏰</div>
          <div className="card-content">
            <div className="card-value">{upcomingEvents.length}</div>
            <div className="card-label">Upcoming Events</div>
          </div>
        </div>

        <div className="summary-card completed-card">
          <div className="card-icon">✓</div>
          <div className="card-content">
            <div className="card-value">{pastEvents.length}</div>
            <div className="card-label">Completed Events</div>
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="finance-controls">
        <input
          type="text"
          placeholder="Search by event name or venue..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="finance-search"
        />
      </div>

      {/* Events Table */}
      <div className="finance-table-container">
        <table className="finance-table">
          <thead>
            <tr>
              <th>Event Name</th>
              <th>Date</th>
              <th>Venue</th>
              <th>Packages</th>
              <th>Guests</th>
              <th>Revenue</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEvents.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-events">
                  {searchTerm ? 'No events match your search' : 'No bookings yet'}
                </td>
              </tr>
            ) : (
              filteredEvents
                .sort((a, b) => new Date(b.startDate || b.date) - new Date(a.startDate || a.date))
                .map(event => (
                  <tr key={event._id} className="table-row">
                    <td className="event-name-cell">
                      <strong>{event.name || 'Unnamed Event'}</strong>
                    </td>
                    <td>{formatDate(event.startDate || event.date)}</td>
                    <td>{event.venue || 'J49 Banquet Hall'}</td>
                    <td className="packages-cell">
                      {getPackageSummary(event)}
                    </td>
                    <td className="guests-cell">
                      {event.metadata?.guestCount || 0}
                    </td>
                    <td className="revenue-cell">
                      <strong>{formatCurrency(event.metadata?.totalCost)}</strong>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="view-btn"
                        onClick={() => openOverrideModal(event)}
                      >
                        Finance Override
                      </button>
                    </td>
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>

      {/* Revenue Breakdown */}
      {filteredEvents.length > 0 && (
        <div className="revenue-breakdown">
          <h3>Revenue Breakdown</h3>
          <div className="breakdown-grid">
            <div className="breakdown-item">
              <span className="breakdown-label">Upcoming Events:</span>
              <span className="breakdown-value">
                {formatCurrency(upcomingEvents.reduce((sum, e) => sum + (e.metadata?.totalCost || 0), 0))}
              </span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-label">Past Events:</span>
              <span className="breakdown-value">
                {formatCurrency(pastEvents.reduce((sum, e) => sum + (e.metadata?.totalCost || 0), 0))}
              </span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-label">Average per Event:</span>
              <span className="breakdown-value">
                {formatCurrency(totalRevenue / events.length)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Finance Override Modal */}
      {showOverrideModal && selectedEvent && (
        <div className="modal-overlay" onClick={closeOverrideModal}>
          <div className="modal-content finance-override-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Finance Override - {selectedEvent.name}</h2>
              <button className="modal-close" onClick={closeOverrideModal}>&times;</button>
            </div>

            <div className="modal-body">
              {/* Package Pricing Breakdown */}
              <div className="pricing-section">
                <h3 className="section-title">Package Pricing</h3>
                <div className="pricing-breakdown">
                  {selectedEvent.metadata?.packages?.food && (
                    <div className="pricing-line">
                      <span>Food: {selectedEvent.metadata.packages.food.name}</span>
                      <span>{formatCurrency(selectedEvent.metadata.packages.food.totalCost)}</span>
                    </div>
                  )}
                  {selectedEvent.metadata?.packages?.alcohol && (
                    <div className="pricing-line">
                      <span>Beverages: {selectedEvent.metadata.packages.alcohol.name}</span>
                      <span>{formatCurrency(selectedEvent.metadata.packages.alcohol.totalCost)}</span>
                    </div>
                  )}
                  {selectedEvent.metadata?.packages?.music && (
                    <div className="pricing-line">
                      <span>Entertainment: {selectedEvent.metadata.packages.music.name}</span>
                      <span>{formatCurrency(selectedEvent.metadata.packages.music.totalCost)}</span>
                    </div>
                  )}
                  {selectedEvent.metadata?.packages?.decor && (
                    <div className="pricing-line">
                      <span>Decor: {selectedEvent.metadata.packages.decor.name}</span>
                      <span>{formatCurrency(selectedEvent.metadata.packages.decor.totalCost)}</span>
                    </div>
                  )}
                  <div className="pricing-divider"></div>
                  {selectedEvent.metadata?.discount > 0 && (
                    <div className="pricing-line">
                      <span>Bulk Discount ({selectedEvent.metadata.discountPercentage}%):</span>
                      <span className="discount">-{formatCurrency(selectedEvent.metadata.discount)}</span>
                    </div>
                  )}
                  <div className="pricing-divider"></div>
                  <div className="pricing-line total">
                    <span>Auto-Calculated Total:</span>
                    <span>{formatCurrency(selectedEvent.metadata?.totalCost)}</span>
                  </div>
                </div>
              </div>

              {/* Finance Manager Adjustments */}
              <div className="adjustments-section">
                <h3 className="section-title">Finance Manager Adjustments</h3>

                {!addingLineItem && (
                  <button
                    className="add-line-item-btn"
                    onClick={() => setAddingLineItem(true)}
                  >
                    + Add Line Item
                  </button>
                )}

                {addingLineItem && (
                  <div className="inline-form">
                    <input
                      type="text"
                      placeholder="Description (e.g., Loyalty discount)"
                      value={newLineItem.description}
                      onChange={(e) => setNewLineItem({...newLineItem, description: e.target.value})}
                      className="line-item-input"
                      maxLength={500}
                    />
                    <input
                      type="number"
                      placeholder="Amount (negative for discount)"
                      value={newLineItem.amount}
                      onChange={(e) => setNewLineItem({...newLineItem, amount: e.target.value})}
                      className="line-item-amount"
                      step="0.01"
                    />
                    <button className="add-btn" onClick={handleAddLineItem}>Add</button>
                    <button className="cancel-btn" onClick={() => {
                      setAddingLineItem(false);
                      setNewLineItem({ description: '', amount: '' });
                    }}>Cancel</button>
                  </div>
                )}

                {customLineItems.length > 0 && (
                  <div className="line-items-list">
                    {customLineItems.map((item, index) => (
                      <div key={index} className="line-item">
                        <span className="item-description">{item.description}</span>
                        <span className={`item-amount ${item.amount < 0 ? 'discount' : 'charge'}`}>
                          {item.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(item.amount))}
                        </span>
                        <button
                          className="remove-item-btn"
                          onClick={() => handleRemoveLineItem(index)}
                          title="Remove item"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                    <div className="pricing-divider"></div>
                    <div className="pricing-line total final">
                      <span>Final Total:</span>
                      <span>{formatCurrency(calculateFinalTotal())}</span>
                    </div>
                  </div>
                )}

                <div className="notes-section">
                  <label className="notes-label">Notes:</label>
                  <textarea
                    value={overrideNotes}
                    onChange={(e) => setOverrideNotes(e.target.value)}
                    placeholder="Add notes for this pricing adjustment (e.g., Special pricing for repeat customer)"
                    className="notes-textarea"
                    rows={4}
                    maxLength={500}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <div className="modal-footer-left">
                {(customLineItems.length > 0 || overrideNotes.trim()) && (
                  <button
                    className="delete-btn"
                    onClick={() => setRemoveConfirm(true)}
                    disabled={saving}
                  >
                    Remove Override
                  </button>
                )}
              </div>
              <div className="modal-footer-right">
                <button className="cancel-btn" onClick={closeOverrideModal} disabled={saving}>
                  Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={handleSaveOverride}
                  disabled={saving || (customLineItems.length === 0 && !overrideNotes.trim())}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        show={removeConfirm}
        onConfirm={handleRemoveOverride}
        onCancel={() => setRemoveConfirm(false)}
        title="Remove Finance Override"
        message="Are you sure you want to remove this finance override? The event will revert to automatic calculations."
        itemType="finance override"
        confirmText="Remove"
      />
    </div>
  );
};

export default FinanceDashboardPage;
